import { useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';

const fmt = (v) => v != null ? `£${Number(v).toFixed(2)}` : '—';

const ISSUE_LABELS = {
  missing: 'No part number',
  prefixed: 'Our own prefix',
  name: 'Product name, not a part number',
};

// Supplier Matching on the Product Catalog. Each night Ops looks catalogue
// products up at TD SYNNEX, Midwich and OneMedia by manufacturer part number
// and adds the price where exactly one product matches (src/api/supplierMatch.js).
// This panel holds the two things that need a person: the matches where a
// distributor has more than one product with the part number, and the products
// whose part number cannot be matched on at all.
export default function SupplierMatchingPanel({ onClose, onEdit, onChanged }) {
  const [pending, setPending] = useState([]);
  const [issues, setIssues] = useState(null);
  const [issueFilter, setIssueFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [rowState, setRowState] = useState({});

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [m, i] = await Promise.all([
        api.get('product-catalog/supplier-matches'),
        api.get('product-catalog/part-number-issues'),
      ]);
      setPending(Array.isArray(m) ? m : []);
      setIssues(i);
    } catch (e) {
      setLoadError(e?.data?.error || 'Could not load supplier matching.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const r = await api.post('product-catalog/supplier-matches/run', {});
      setRunResult({ ok: true, ...r });
      await load();
      if (r.added) onChanged?.();
    } catch (e) {
      setRunResult({ ok: false, error: e?.data?.error || 'The check failed.' });
    } finally {
      setRunning(false);
    }
  };

  const decide = async (m, action) => {
    setRowState(s => ({ ...s, [m.id]: { busy: true } }));
    try {
      await api.post(`product-catalog/supplier-matches/${m.id}/${action}`, {});
      setRowState(s => ({ ...s, [m.id]: { done: action } }));
      // Choosing one settles the others for the same product and supplier.
      await load();
      if (action === 'approve') onChanged?.();
    } catch (e) {
      setRowState(s => ({ ...s, [m.id]: { error: e?.data?.error || 'That did not save.' } }));
    }
  };

  const byProduct = pending.reduce((acc, m) => {
    const key = `${m.product_catalog_id}|${m.supplier_name}`;
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});

  const issueRows = (issues?.issues || []).filter(p => issueFilter === 'all' || p.issue === issueFilter);

  return (
    <Modal title="Supplier Matching" wide onClose={onClose} footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
    }>
      <div className="space-y-4 text-[10px]">
        <div className="flex items-start justify-between gap-3">
          <p className="text-gray-500">
            Each night Ops looks products up at TD SYNNEX, Midwich and OneMedia by manufacturer part number,
            and adds the supplier price where exactly one product matches. Prices added this way keep themselves
            up to date. A price already on a product is never changed, and one you delete is not added back.
          </p>
          <div className="flex-shrink-0 text-right">
            <button className="btn-primary text-[9px] px-2 py-1" onClick={runNow} disabled={running}>
              {running ? 'Checking…' : 'Check now'}
            </button>
            {runResult && (
              <div className={`text-[8px] mt-1 max-w-[220px] ${runResult.ok ? 'text-gray-500' : 'text-red-700'}`}>
                {runResult.ok
                  ? `Checked ${runResult.checked} product${runResult.checked === 1 ? '' : 's'}: ${runResult.added} price${runResult.added === 1 ? '' : 's'} added, ${runResult.pending} to choose.${runResult.errors?.length ? ` ${runResult.errors.length} lookup${runResult.errors.length === 1 ? '' : 's'} failed.` : ''}`
                  : runResult.error}
              </div>
            )}
          </div>
        </div>

        {loading && <div className="text-gray-400">Loading…</div>}
        {loadError && <div className="text-red-700">{loadError}</div>}

        {!loading && !loadError && (
          <>
            <section>
              <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">Choose a match ({Object.keys(byProduct).length})</div>
              {Object.keys(byProduct).length === 0 && (
                <div className="text-gray-400">Nothing to choose. Every match found so far was unambiguous.</div>
              )}
              <div className="space-y-2">
                {Object.entries(byProduct).map(([key, ms]) => (
                  <div key={key} className="border border-gray-200 rounded p-2 bg-white">
                    <div className="mb-1">
                      <span className="font-mono font-bold text-navy">{ms[0].product_code}</span>
                      <span className="text-gray-600 ml-2">{ms[0].product_description}</span>
                      <span className="text-gray-400 ml-2">part number {ms[0].product_part_number}</span>
                    </div>
                    <div className="text-[8px] text-gray-500 mb-1">{ms[0].supplier_name} has {ms.length} products with this part number:</div>
                    {ms.map(m => (
                      <div key={m.id} className="flex items-center gap-3 py-1 border-t border-gray-100">
                        <span className="font-mono text-[9px] text-gray-700 w-28 truncate">{m.supplier_sku}</span>
                        <span className="flex-1 min-w-0 truncate text-gray-600">{m.description}</span>
                        <span className="font-semibold text-emerald-700">{fmt(m.trade_price)}</span>
                        {rowState[m.id]?.error && <span className="text-red-700 text-[8px]">{rowState[m.id].error}</span>}
                        <button className="text-[8px] font-semibold text-emerald-700 underline" disabled={rowState[m.id]?.busy} onClick={() => decide(m, 'approve')}>Use this one</button>
                        <button className="text-[8px] text-gray-500 underline" disabled={rowState[m.id]?.busy} onClick={() => decide(m, 'reject')}>Not this</button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[9px] font-bold text-gray-500 uppercase">
                  Part numbers to fix ({issues?.issues?.length || 0} of {issues?.total_active || 0} active products)
                </div>
                <div className="flex gap-1">
                  {['all', 'missing', 'prefixed', 'name'].map(f => (
                    <button
                      key={f}
                      onClick={() => setIssueFilter(f)}
                      className={`px-2 py-0.5 rounded text-[8px] border ${issueFilter === f ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200'}`}
                    >
                      {f === 'all' ? 'All' : ISSUE_LABELS[f]} {f === 'all' ? '' : `(${issues?.counts?.[f] || 0})`}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[8px] text-gray-400 mb-1">
                These cannot be matched until they carry the manufacturer's own part number, as printed on the
                manufacturer's datasheet. Edit one, correct the part number and save: it is looked up straight away.
              </p>
              {issueRows.length === 0 ? (
                <div className="text-gray-400">None.</div>
              ) : (
                <div className="overflow-x-auto"><table className="w-full text-[9px]">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-200">
                      <th className="py-1 pr-2">Code</th>
                      <th className="py-1 pr-2">Description</th>
                      <th className="py-1 pr-2">Manufacturer</th>
                      <th className="py-1 pr-2">Part number</th>
                      <th className="py-1 pr-2">Problem</th>
                      <th className="py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueRows.map(p => (
                      <tr key={p.id} className="border-b border-gray-100">
                        <td className="py-1 pr-2 font-mono text-navy">{p.code}</td>
                        <td className="py-1 pr-2 text-gray-700">{p.description}</td>
                        <td className="py-1 pr-2 text-gray-500">{p.manufacturer || '—'}</td>
                        <td className="py-1 pr-2 font-mono text-gray-500">{p.manufacturer_part_number || '—'}</td>
                        <td className="py-1 pr-2 text-amber-700">{p.issue_message}</td>
                        <td className="py-1 text-right">
                          {rowState[p.id]?.error && <span className="text-red-700 text-[8px] mr-2">{rowState[p.id].error}</span>}
                          <button
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] font-semibold rounded hover:bg-gray-200"
                            onClick={() => Promise.resolve(onEdit(p.id)).catch(e =>
                              setRowState(s => ({ ...s, [p.id]: { error: e?.data?.error || 'Could not open that product.' } })))}
                          >Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}
